import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useContext, useState } from 'react';
import { object, ObjectSchema, string } from 'yup';
import MessagesApi from '../../apis/MessagesApi';
import FormikTextField from '../../components/FormikTextField/FormikTextField';
import Logo from '../../components/Logo/Logo';
import SnackbarContext from '../../contexts/SnackbarContext';
import { MessageContactUsDto } from '../../generated-types/messageContactUsDto';
import useApi from '../../lib/useApi';

/**
 * A view that exists for anyone to send us a message
 */
const ContactView = () => {
  const schema: ObjectSchema<MessageContactUsDto> = object({
    name: string().defined('Required'),
    email: string().defined('Required').email('Email must be valid'),
    message: string().defined('Required'),
  });
  const messagesApi = useApi(MessagesApi);
  const snackbarContext = useContext(SnackbarContext);
  const [sent, setSent] = useState(false);

  const formik = useFormik<MessageContactUsDto>({
    initialValues: {
      name: '',
      email: '',
      message: '',
    },
    validationSchema: schema,
    onSubmit: (val) => {
      messagesApi
        .ContactUsSendMessage(val)
        .then(() => {
          setSent(true);
          snackbarContext.setAlert(
            <Alert variant="filled" severity="success">
              Successfully sent the message!
            </Alert>
          );
        })
        .catch(() => {
          snackbarContext.setAlert(
            <Alert variant="filled" severity="error">
              Failed to send the message. Please try again.
            </Alert>
          );
        })
        .finally(() => {
          formik.setSubmitting(false);
          formik.resetForm();
          snackbarContext.triggerSnackbar();
        });
    },
  });

  return (
    <Box
      width="100%"
      // display="flex"
      // alignItems="center"
      // justifyContent="center"
      sx={{
        backgroundImage: 'url(/contact-us-background.png)',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
      }}
    >
      <Container
        maxWidth="md"
        sx={{ height: '100%', overflow: 'auto', paddingY: '1em' }}
      >
        <Card
          sx={{
            padding: '2em',
          }}
        >
          {sent ? (
            <Box display="flex" flexDirection="column" alignItems="center">
              <CheckCircleOutlineRoundedIcon
                color="success"
                sx={{ fontSize: '5em' }}
              />
              <Typography fontSize="3em" fontWeight={500}>
                Success!
              </Typography>
              <Typography marginBottom="2em">
                Your message has been sent successfully
              </Typography>
              <Button variant="contained" onClick={() => setSent(false)}>
                Send another
              </Button>
            </Box>
          ) : (
            <Grid container spacing="1em">
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                <Typography component="h1" fontSize="3em" gutterBottom>
                  Contact Us
                </Typography>
                <Typography gutterBottom>
                  Not sure what you need? Whether you have a question, need
                  assistance, or just want to connect, we&apos;re here to help.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginTop="1em"
                  marginBottom="2.5em"
                >
                  <Box>
                    <Typography
                      component="h2"
                      fontSize="1.5em"
                      lineHeight="1em"
                    >
                      We&apos;d love to hear from you!
                    </Typography>
                    <Typography component="h2" fontSize="1.5em">
                      Let&apos;s get in touch
                    </Typography>
                  </Box>
                  <Logo sizeEm={2} />
                </Box>
                <form onSubmit={formik.handleSubmit}>
                  <FormikTextField
                    formik={formik}
                    fullWidth
                    label="Name"
                    name="name"
                    sx={{
                      marginBottom: '1em',
                    }}
                  />
                  <FormikTextField
                    formik={formik}
                    fullWidth
                    label="Email"
                    name="email"
                    sx={{
                      marginBottom: '1em',
                    }}
                  />
                  <FormikTextField
                    formik={formik}
                    fullWidth
                    label="Message"
                    name="message"
                    multiline
                    rows={4}
                    sx={{
                      marginBottom: '1em',
                    }}
                  />
                  <LoadingButton
                    loading={formik.isSubmitting}
                    loadingPosition="end"
                    type="submit"
                    variant="contained"
                    endIcon={<SendRoundedIcon />}
                  >
                    Send Message
                  </LoadingButton>
                </form>
              </Grid>
            </Grid>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default ContactView;
